// packages/app/src/App.tsx
import { DashboardPage, AuditLogPage } from './components/dashboard/DashboardPage';
import { TokensPage } from './components/tokens/TokensPage';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import {
  ScaffolderPage,
  scaffolderPlugin,
} from '@backstage/plugin-scaffolder';
import {
  ScaffolderFieldExtensions,
  createScaffolderFieldExtension,
} from '@backstage/plugin-scaffolder-react';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { TemplateEntityV1beta3 } from '@backstage/plugin-scaffolder-common';

import { NotificationsPage } from '@backstage/plugin-notifications';
import { ActionPage, TestNotificationPage } from '@internal/plugin-action-notifications';

import { entityPage } from './components/catalog/EntityPage';
import { DocsHomePage } from './components/techdocs/DocsHomePage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';

import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';

import { UnifiedThemeProvider } from '@backstage/theme';
import { dtTheme } from './theme';

import { apis, oidcAuthApiRef } from './apis';

import { parseEksTeam } from './utils/eksTeam';

// ── Custom scaffolder field extensions ──────────────────────────────────────
import { UserTeamPickerComponent }        from './scaffolder/UserTeamPicker';
import { PlatLeadGuardComponent }         from './scaffolder/PlatLeadGuard';
import { TeamLeadGuardComponent }         from './scaffolder/TeamLeadGuard';
import { AdditionalTeamsPickerComponent } from './scaffolder/AdditionalTeamsPicker';
import { ApplicationKeyPickerComponent }  from './scaffolder/ApplicationKeyPicker';
import { ServiceRepoPickerComponent }     from './scaffolder/ServiceRepoPicker';
import { ServiceKeyPickerComponent }      from './scaffolder/ServiceKeyPicker';
import { ApiGatewayPathPickerComponent }  from './scaffolder/ApiGatewayPathPicker';

const UserTeamPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'UserTeamPicker',
    component: UserTeamPickerComponent,
  }),
);

const PlatLeadGuardExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'PlatLeadGuard',
    component: PlatLeadGuardComponent,
  }),
);

const TeamLeadGuardExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'TeamLeadGuard',
    component: TeamLeadGuardComponent,
  }),
);

const AdditionalTeamsPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'AdditionalTeamsPicker',
    component: AdditionalTeamsPickerComponent,
  }),
);

const ApplicationKeyPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ApplicationKeyPicker',
    component: ApplicationKeyPickerComponent,
  }),
);

const ServiceRepoPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ServiceRepoPicker',
    component: ServiceRepoPickerComponent,
  }),
);

const ServiceKeyPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ServiceKeyPicker',
    component: ServiceKeyPickerComponent,
  }),
);

const ApiGatewayPathPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ApiGatewayPathPicker',
    component: ApiGatewayPathPickerComponent,
  }),
);

// ── Template visibility rules ────────────────────────────────────────────────
const PLAT_LEAD_ONLY = ['create-team'];                                // only plat-leads
const LEAD_ONLY      = ['create-basic-application', 'create-service']; // any lead

// ── Identity ref ──────────────────────────────────────────────────────────────
// Plain object ref — updated by IdentityProvider after async fetch.
// templateFilter reads from this ref. IdentityProvider calls forceRender()
// after updating so React re-renders and templateFilter sees correct values.
// ScaffolderPage stays literally in the route tree (Backstage scanner requirement).
const identityRef = {
  isPlatLead: false,
  isLead: false,
};

const templateFilter = (entity: TemplateEntityV1beta3): boolean => {
  if (PLAT_LEAD_ONLY.includes(entity.metadata.name)) return identityRef.isPlatLead;
  if (LEAD_ONLY.includes(entity.metadata.name))      return identityRef.isLead;
  return true;
};

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => {
      const host = window.location.hostname;
      const isEksDev  = host === 'self-service.toolsdev.dtcloudhosting.com';
      const isEksProd = host === 'self-service.tools.dtcloudhosting.com';
      const useOkta   = isEksDev || isEksProd;
      return (
        <SignInPage
          {...props}
          auto
          providers={[
            {
              id: 'oidc',
              title:   useOkta ? 'Okta'     : 'Keycloak',
              message: useOkta ? 'Sign in with Okta' : 'Sign in with Keycloak',
              apiRef: oidcAuthApiRef,
            },
          ]}
        />
      );
    },
  },
  themes: [
    {
      id: 'dt-theme',
      title: 'DT Theme',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={dtTheme}>{children}</UnifiedThemeProvider>
      ),
    },
  ],
});

// ── IdentityProvider ──────────────────────────────────────────────────────────
// Resolves identity once on mount.
// Updates identityRef (for templateFilter) then calls forceRender so React
// re-renders the whole tree — templateFilter then reads correct values.
// ScaffolderPage stays directly in routes so Backstage's scanner finds it.
const IdentityProvider = ({ children }: { children: ReactNode }) => {
  const identityApi = useApi(identityApiRef);
  const [, forceRender] = useState(0);

  useEffect(() => {
    identityApi.getBackstageIdentity().then(result => {
      const refs = result.ownershipEntityRefs ?? [];
      identityRef.isPlatLead = refs.some(ref => {
        const parsed = parseEksTeam(ref);
        return parsed !== null && parsed.isPlatform && parsed.role === 'lead';
      });
      identityRef.isLead     = refs.some(ref => {
        const parsed = parseEksTeam(ref);
        return parsed !== null && parsed.role === 'lead';
      });
      forceRender(n => n + 1); // triggers re-render → templateFilter re-runs with correct values
    });
  }, [identityApi]);

  return <>{children}</>;
};

// ── routes ───────────────────────────────────────────────────────────────────
// ScaffolderPage MUST be the literal element value — Backstage's static
// route scanner requires it to be directly visible in this JSX tree.
const routes = (
  <FlatRoutes>
    <Route path="/" element={<DashboardPage />} />
    <Route path="/audit-log" element={<AuditLogPage />} />
    <Route path="/tokens" element={<TokensPage />} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/action-notifications/actions/:actionId" element={<ActionPage />} />
    <Route path="/action-notifications/test" element={<TestNotificationPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>

    <Route path="/docs" element={<TechDocsIndexPage />}>
      <DocsHomePage />
    </Route>
    <Route path="/docs/:namespace/:kind/:name/*" element={<TechDocsReaderPage />}>
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>

    <Route
      path="/create"
      element={<ScaffolderPage templateFilter={templateFilter} />}
    >
      <ScaffolderFieldExtensions>
        <UserTeamPickerExtension />
        <PlatLeadGuardExtension />
        <TeamLeadGuardExtension />
        <AdditionalTeamsPickerExtension />
        <ApplicationKeyPickerExtension />
        <ServiceRepoPickerExtension />
        <ServiceKeyPickerExtension />
        <ApiGatewayPathPickerExtension />
      </ScaffolderFieldExtensions>
    </Route>

    <Route path="/api-docs" element={<ApiExplorerPage />} />

    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />

    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>

    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <IdentityProvider>
        <Root>{routes}</Root>
      </IdentityProvider>
    </AppRouter>
  </>,
);